import { Receipt } from 'lucide-react'
import { Button } from '@/components/ui'
import { TransactionRow } from './TransactionRow'
import type { Transaction } from '@/types'

interface TransactionsListProps {
  transactions: Transaction[]
  isLoading?: boolean
  onFlag?: (transactionId: string) => void
  onUnflag?: (transactionId: string) => void
  onViewDetails?: (transactionId: string) => void
  pagination?: {
    page: number
    page_size: number
    total_pages: number
    total_count: number
  }
  onPageChange?: (page: number) => void
}

function TransactionSkeleton() {
  return (
    <div className="flex animate-pulse items-center gap-3 px-4 py-3">
      <div className="h-10 w-10 shrink-0 rounded-xl bg-gray-100" />
      <div className="flex-1">
        <div className="h-4 w-48 rounded bg-gray-100" />
        <div className="mt-1.5 h-3 w-24 rounded bg-gray-100" />
      </div>
      <div className="flex flex-col items-end gap-1.5">
        <div className="h-4 w-16 rounded bg-gray-100" />
        <div className="h-3 w-10 rounded bg-gray-100" />
      </div>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gray-400/10 text-gray-400">
        <Receipt className="h-6 w-6" />
      </span>
      <h3 className="mt-4 text-base font-semibold text-gray-900">Nothing here yet</h3>
      <p className="mt-1 max-w-sm text-sm text-gray-500">
        Transactions will appear here once connected accounts sync.
      </p>
    </div>
  )
}

export function TransactionsList({
  transactions,
  isLoading,
  onFlag,
  onUnflag,
  onViewDetails,
  pagination,
  onPageChange,
}: TransactionsListProps) {
  if (isLoading) {
    return (
      <div className="divide-y divide-gray-100">
        {Array.from({ length: 5 }).map((_, i) => (
          <TransactionSkeleton key={i} />
        ))}
      </div>
    )
  }

  if (transactions.length === 0) {
    return <EmptyState />
  }

  return (
    <>
      <div className="divide-y divide-gray-100">
        {transactions.map((transaction) => (
          <TransactionRow
            key={transaction.id}
            transaction={transaction}
            onFlag={onFlag}
            onUnflag={onUnflag}
            onViewDetails={onViewDetails}
          />
        ))}
      </div>

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && onPageChange && (
        <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3">
          <p className="text-xs text-gray-400">
            <span className="font-rounded tabular-nums">
              {(pagination.page - 1) * pagination.page_size + 1}–
              {Math.min(pagination.page * pagination.page_size, pagination.total_count)}
            </span>{' '}
            of <span className="font-rounded tabular-nums">{pagination.total_count}</span>
          </p>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
            >
              Previous
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.total_pages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </>
  )
}
